// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';

// material ui
import { Box, Grid, Stack, Typography } from '@mui/material';

// api
import { useGetEnergySavingsQuery } from '@/api/energy-analysis/calculateEnergySavingsApiSlice';
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import { WritableOnly } from '@/components/access-control';
import PageContainer from '@/components/layout/PageContainer';
import { SubmitButton } from '@/components/ui/button/SubmitButton';

// feature-components
import DeleteInitiativeDialog from '@/features/energyxplorer/components/initiatives/DeleteInitiativeDialog';
import InitiativeCard from '@/features/energyxplorer/components/initiatives/InitiativeCard';
import InitiativeChartDrawer from '@/features/energyxplorer/components/initiatives/InitiativeChartDrawer';
import InitiativeDataGrid from '@/features/energyxplorer/components/initiatives/InitiativeDataGrid';

// components
import ErrorBoundary from '@/routes/-ErrorBoundary.tsx';

const getEstimatedCO2eSavingsKg = (initiative) => {
  const s = initiative?.energySavings;
  if (!s) return 0;
  const adv = s.ForecastedSavings?.AdvEstimatedTotalSavingsCO2e / 1000;
  const bas = s.ForecastedSavings?.BasicEstimatedTotalSavingsCO2e / 1000;

  // prefer the flagged branch, fall back to the other if missing
  const v = s.AdvancedModelUsed ? (adv ?? bas) : (bas ?? adv);
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const STATES = ['Created', 'Initialized', 'Active', 'Done', 'Archived'];

function getActualEnergyUsageKWh(i) {
  const s = i?.energySavings;
  if (!s) return 0;

  const adv = s.AdvancedEnergySavings?.EnergySavingsSummary?.ActualEnergyUsage;
  const bas = s.BasicEnergySavings?.EnergySavingsSummary?.ActualEnergyUsage;
  // prefer the flagged branch, fall back to the other if missing
  const v = s.AdvancedModelUsed ? (adv ?? bas) : (bas ?? adv);
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function getEstimatedSavingsToDateKWh(i) {
  const s = i?.energySavings;
  if (!s) return 0;
  const adv = s.ForecastedSavings?.AdvEstimatedTotalSavings;
  const bas = s.ForecastedSavings?.BasicEstimatedTotalSavings;

  // prefer the flagged branch, fall back to the other if missing
  const v = s.AdvancedModelUsed ? (adv ?? bas) : (bas ?? adv);
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// Generic grouper: sums a value getter by initiative.state
function sumByState(initiatives, valueGetter) {
  const acc = {
    Created: 0,
    Initialized: 0,
    Active: 0,
    Done: 0,
    Archived: 0,
  };

  for (const i of initiatives || []) {
    const raw = i?.state || '';
    const state = STATES.find(
      (s) => s.toLowerCase() === String(raw).toLowerCase(),
    );
    if (!state) continue; // skip unknown/missing states
    acc[state] += valueGetter(i);
  }

  return acc;
}

// Public helpers

function getActualEnergyUsageByState(enrichedInitiatives) {
  return sumByState(enrichedInitiatives, getActualEnergyUsageKWh);
}

function getEstimatedSavingsToDateByState(enrichedInitiatives) {
  return sumByState(enrichedInitiatives, getEstimatedSavingsToDateKWh);
}

function getEstimatedCO2eSavingsToDateKg(enrichedInitiatives) {
  return sumByState(enrichedInitiatives, getEstimatedCO2eSavingsKg);
}

// Format helper (round before Intl)
const fmt0 = (n) => new Intl.NumberFormat('da-DK').format(Math.round(n || 0));

export const Route = createFileRoute(
  '/$accountId/_user/energyxplorer/deepdive/$energyGroupId/energy-initiatives/',
)({
  component: RouteComponent,
  errorComponent: ErrorBoundary,
});

function RouteComponent() {
  const { accountId, energyGroupId } = useParams({ strict: false });

  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    { skip: !energyGroupId, pollingInterval: 60000 }, // 1 minute
  );

  const {
    data: energySavingsData,
    isLoading: isLoadingEnergySavingsData,
    isFetching: isFetchingEnergySavingsData,
  } = useGetEnergySavingsQuery(
    {
      energyGroupId: energyGroupId,
      accountId: accountId,
    },
    {
      skip: !energyGroupId,
    },
  );

  const { t } = useTranslation('energyxplorer-initiatives-tab');
  const { t: tEsg } = useTranslation('energyxplorer-esg');

  const [selectedInitiative, setSelectedInitiative] = useState(null);
  const navigate = useNavigate();

  const handleCreateInitiative = () => {
    navigate({
      to: './create',
      search: (prev: Record<string, string>) => ({ ...prev }),
    });
  };

  const handleEditInitiative = (initiative) => {
    if (!initiative) return;
    navigate({
      to: `./${initiative.id}/edit`,
      search: (prev: Record<string, string>) => ({ ...prev }),
    });
  };

  const [openDelete, setOpenDelete] = useState(false);

  const handleDeleteInitiative = (initiative) => {
    if (!initiative) return;
    setSelectedInitiative(initiative);
    setOpenDelete(true);
  };

  const [openPlot, setOpenPlot] = useState(false);

  const handleViewInitiative = (initiative) => {
    navigate({
      to: `./${initiative.id}`,
      search: (prev: Record<string, string>) => ({ ...prev }),
    });
  };

  const [initiativeStateFilter, setInitiativeStateFilter] = useState(null);

  const handleCloseInitiativePlotDialog = () => {
    setOpenPlot(false);
    setSelectedInitiative(null);
  };

  const initiatives = selectedEnergyGroup?.energyInitiatives || [];
  const savings = energySavingsData?.InitiativeEnergySavings || [];

  // Create a lookup map from InitiativeEnergySavings
  const savingsMap = new Map(savings.map((s) => [s.Id, s]));

  // Merge matching savings data into each initiative
  const enrichedInitiatives = initiatives.map((initiative) => {
    const savingsData = savingsMap.get(initiative.energyInitiativeId);
    return {
      ...initiative,
      energySavings: savingsData || null,
    };
  });

  const actualEnergyUsageByState =
    getActualEnergyUsageByState(enrichedInitiatives);

  const estimatedSavingsToDateByState =
    getEstimatedSavingsToDateByState(enrichedInitiatives);

  const estimatedCO2eSavingsToDateByState =
    getEstimatedCO2eSavingsToDateKg(enrichedInitiatives);

  const initiativeCount = (() => {
    let archivedCount = 0;
    let activeCount = 0;
    let createdCount = 0;

    (enrichedInitiatives || []).forEach((i) => {
      const state = i?.state || '';
      const s = state.toLowerCase();
      if (s === 'archived') archivedCount++;
      else if (s === 'active' || s === 'done') activeCount++;
      else if (s === 'created' || s === 'initialized') createdCount++;
    });
    return { archivedCount, activeCount, createdCount };
  })();

  return (
    <PageContainer sx={{ pt: 0 }}>
      {enrichedInitiatives.length !== 0 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <>
            <Grid size="grow">
              <InitiativeCard
                icon={<FolderRoundedIcon />}
                tooltip={t('tooltips.report_ready_card')}
                title={t('cards.report_ready_card.title')}
                caption={
                  <>
                    {initiativeCount.archivedCount}{' '}
                    {t('cards.report_ready_card.caption')}
                  </>
                }
                subtitle={t('cards.report_ready_card.subtitle')}
                loading={
                  isLoadingEnergySavingsData || isFetchingEnergySavingsData
                }
                dimmed={
                  initiativeStateFilter !== null &&
                  initiativeStateFilter !== 'Archived'
                }
                onClick={() =>
                  initiativeStateFilter === 'Archived'
                    ? setInitiativeStateFilter(null)
                    : setInitiativeStateFilter('Archived')
                }
                value={
                  <>
                    <Typography variant="h5">
                      {fmt0(estimatedSavingsToDateByState?.Archived)}{' '}
                      {t('consumption_unit', { ns: 'energyxplorer-esg' })}
                    </Typography>
                    <Typography variant="body1">
                      /{' '}
                      {t('kanban.savings.co2e_format', {
                        value: fmt0(
                          estimatedCO2eSavingsToDateByState?.Archived,
                        ),
                      })}
                    </Typography>
                  </>
                }
              />
            </Grid>

            <Grid size="grow">
              <InitiativeCard
                icon={<QueryStatsRoundedIcon />}
                tooltip={t('tooltips.active_card')}
                title={t('cards.active_card.title')}
                caption={
                  initiativeCount.activeCount > 0
                    ? `${initiativeCount.activeCount} ${t('cards.active_card.caption')}`
                    : t('cards.no_action_required')
                }
                subtitle={t('cards.active_card.subtitle')}
                loading={
                  isLoadingEnergySavingsData || isFetchingEnergySavingsData
                }
                dimmed={
                  initiativeStateFilter !== null &&
                  initiativeStateFilter !== 'Done'
                }
                onClick={() =>
                  initiativeStateFilter === 'Done'
                    ? setInitiativeStateFilter(null)
                    : setInitiativeStateFilter('Done')
                }
                value={
                  <Typography variant="h5">
                    {fmt0(
                      actualEnergyUsageByState?.Done +
                        actualEnergyUsageByState?.Active,
                    )}{' '}
                    {tEsg('consumption_unit')}
                  </Typography>
                }
              />
            </Grid>

            <Grid size="grow">
              <InitiativeCard
                icon={<AutoAwesomeRoundedIcon />}
                tooltip={t('tooltips.started_card')}
                title={t('cards.started_card.title')}
                caption={
                  initiativeCount.createdCount > 0
                    ? `${initiativeCount.createdCount} ${t('cards.started_card.caption')}`
                    : t('cards.no_action_required')
                }
                subtitle={t('cards.started_card.subtitle')}
                loading={
                  isLoadingEnergySavingsData || isFetchingEnergySavingsData
                }
                dimmed={
                  initiativeStateFilter !== null &&
                  initiativeStateFilter !== 'Created'
                }
                onClick={() =>
                  initiativeStateFilter === 'Created'
                    ? setInitiativeStateFilter(null)
                    : setInitiativeStateFilter('Created')
                }
                value={
                  <Typography variant="h5">
                    {fmt0(
                      actualEnergyUsageByState?.Initialized +
                        actualEnergyUsageByState?.Created,
                    )}{' '}
                    {t('consumption_unit', { ns: 'energyxplorer-esg' })}
                  </Typography>
                }
              />
            </Grid>
          </>
        </Grid>
      )}
      <Stack spacing={2}>
        <Box display="flex" justifyContent="end" alignItems="center">
          <WritableOnly>
            <SubmitButton
              data-testid="initiative-add-button"
              type="button"
              startIcon={
                <AddRoundedIcon
                  sx={{ transition: 'margin-right 0.1s ease-in-out' }}
                />
              }
              onClick={handleCreateInitiative}
            >
              {t('button_labels.add_initiative')}
            </SubmitButton>
          </WritableOnly>
        </Box>

        <InitiativeDataGrid
          initiatives={enrichedInitiatives}
          energyGroup={selectedEnergyGroup}
          handleViewInitiative={handleViewInitiative}
          handleEditInitiative={handleEditInitiative}
          handleDeleteInitiative={handleDeleteInitiative}
          isLoading={isLoadingEnergySavingsData || isFetchingEnergySavingsData}
          initiativeStateFilter={initiativeStateFilter}
        />

        <DeleteInitiativeDialog
          open={openDelete}
          onClose={() => setOpenDelete(false)}
          energyGroup={selectedEnergyGroup}
          initiative={selectedInitiative}
        />

        {openPlot && (
          <InitiativeChartDrawer
            open={openPlot}
            onClose={handleCloseInitiativePlotDialog}
            selectedInitiative={selectedInitiative}
          />
        )}
      </Stack>
    </PageContainer>
  );
}
